import { useEffect, useState } from 'react'
import { Clock, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useHeuresNumero, useModifierHeuresNumero, type HeuresNumero, type JourHeures } from '@/api/phoneNumbers'

const JOURS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
type Jour = (typeof JOURS)[number]

const HEURES = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`)
const JOUR_OUVERT: JourHeures = { open: '09:00', close: '17:00' }
const JOUR_FERME: JourHeures = { open: '', close: '' }

/** « Toujours ouvert », « Lun–Ven 09:00–17:00 »… : une grille de 7 jours en une ligne. */
export function resumeHeures(d: Record<Jour, JourHeures>, t: TFunction): string {
  const ouverts = JOURS.filter((j) => d[j].open && d[j].close && d[j].open !== d[j].close)
  if (!ouverts.length) return JOURS.every((j) => !d[j].open && !d[j].close)
    ? t('manager.numbers.hours.always', 'Toujours ouvert')
    : t('manager.numbers.hours.closed', 'Toujours fermé')
  const premier = d[ouverts[0]]
  const memes = ouverts.every((j) => d[j].open === premier.open && d[j].close === premier.close)
  const noms = ouverts.map((j) => t(`manager.schedule.days.${j}`).slice(0, 3))
  const plage = memes ? ` ${premier.open}–${premier.close}` : ''
  return `${noms.length > 2 ? `${noms[0]}–${noms[noms.length - 1]}` : noms.join(', ')}${plage}`
}

/**
 * Heures d'ouverture du numéro : suit l'entreprise par défaut (une ligne),
 * ou ses propres heures (une grille de 7 jours). Modèle Quo : « Business
 * hours » par numéro.
 */
export function HeuresDuNumero({ companyId, numberId, onVoirEntreprise }: { companyId: number; numberId: number; onVoirEntreprise: () => void }) {
  const { t } = useTranslation()
  const { data, isLoading } = useHeuresNumero(companyId, numberId)
  const modifier = useModifierHeuresNumero(companyId, numberId)
  const [jours, setJours] = useState<HeuresNumero['days'] | null>(null)
  const [modifie, setModifie] = useState(false)

  useEffect(() => {
    if (data) {
      setJours(data.days)
      setModifie(false)
    }
  }, [data])

  const resume = (d: HeuresNumero['days']) => resumeHeures(d, t)

  const basculer = async (propres: boolean) => {
    try {
      await modifier.mutateAsync({ use_company_hours: !propres })
    } catch {
      toast.error(t('manager.numbers.hours.error', 'Impossible de modifier les heures.'))
    }
  }

  const enregistrer = async () => {
    if (!jours) return
    try {
      await modifier.mutateAsync({ use_company_hours: false, days: jours })
      toast.success(t('manager.numbers.hours.saved', 'Heures enregistrées.'))
    } catch {
      toast.error(t('manager.numbers.hours.error', 'Impossible de modifier les heures.'))
    }
  }

  const poser = (j: Jour, v: JourHeures) => {
    setJours((prev) => (prev ? { ...prev, [j]: v } : prev))
    setModifie(true)
  }

  if (isLoading || !data || !jours) {
    return <div className="h-12 flex items-center px-4 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /></div>
  }
  const propres = !data.use_company_hours

  return (
    <section className="rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <Clock className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900 dark:text-white">
              {t('manager.numbers.hours.title', "Heures d'ouverture")}
              <span className="ml-2 font-normal text-slate-500 dark:text-slate-400">{resume(data.days)}</span>
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {propres
                ? t('manager.numbers.hours.own', 'Ce numéro a ses propres heures.')
                : t('manager.numbers.hours.company', "Suit les heures de l'entreprise.")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {!propres && (
            <Button variant="ghost" size="sm" onClick={onVoirEntreprise}>
              {t('manager.numbers.hours.editCompany', "Modifier pour l'entreprise")}
            </Button>
          )}
          <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
            {t('manager.numbers.hours.ownToggle', 'Heures propres à ce numéro')}
            <Switch checked={propres} onCheckedChange={(v) => void basculer(v)} disabled={modifier.isPending} />
          </label>
        </div>
      </div>

      {propres && (
        <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-3 space-y-1">
          {JOURS.map((j) => {
            const d = jours[j]
            const actif = !!(d.open && d.close)
            return (
              <div key={j} className={cn('flex items-center gap-3 h-9 text-sm', !actif && 'text-slate-400')}>
                <Switch checked={actif} onCheckedChange={(v) => poser(j, v ? JOUR_OUVERT : JOUR_FERME)} />
                <span className={cn('w-24', actif && 'text-slate-900 dark:text-white')}>{t(`manager.schedule.days.${j}`)}</span>
                {actif ? (
                  <div className="flex items-center gap-2">
                    <select value={d.open} onChange={(e) => poser(j, { ...d, open: e.target.value })} className="h-8 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2">
                      {HEURES.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <span className="text-slate-400">–</span>
                    <select value={d.close} onChange={(e) => poser(j, { ...d, close: e.target.value })} className="h-8 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2">
                      {HEURES.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ) : (
                  <span className="text-xs">{t('manager.schedule.unavailable')}</span>
                )}
              </div>
            )
          })}
          {modifie && (
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => { setJours(data.days); setModifie(false) }}>{t('common.cancel')}</Button>
              <Button size="sm" onClick={() => void enregistrer()} disabled={modifier.isPending}>
                {modifier.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save', 'Enregistrer')}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
